import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api, errText } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { toast } from 'sonner';
import { ScanLine, Loader2, CheckCircle2, AlertCircle, ChevronDown, Trash2, ShieldCheck, Camera, FileUp, X, FileText, Plus, Eye, Paperclip } from 'lucide-react';
import { useLang } from '@/lib/i18n';
import SuggestionHint from '@/components/SuggestionHint';
import { GapChecklist, groupGaps } from '@/components/GapChecklist';

// Must match backend form_scanner.MAX_VISION_PAGES — the number of pages one
// AI pass can actually read. A longer form is finished with a second
// "add the remaining pages" batch (append=true), not by raising this.
const MAX_PAGES = 6;
const MAX_TOTAL_MB = 20;
const MAX_DIMENSION = 2000;   // downscale phone photos to this max edge
const JPEG_QUALITY = 0.82;

/**
 * Compress an image file client-side (canvas downscale → JPEG).
 * Phone cameras produce 3-8MB photos; after compression a page is typically
 * 200-600KB, so a full 8-page form stays far below the upload cap.
 * Falls back to the original file if the browser can't decode it.
 */
/**
 * iPhones shoot HEIC by default. Browsers cannot decode it, so it used to fall
 * through compressImage untouched and reach the vision model labelled JPEG,
 * failing with a generic error. Refuse it up front with a message that says why.
 */
function isHeic(file) {
  return /^image\/hei[cf]/i.test(file?.type || '') || /\.hei[cf]$/i.test(file?.name || '');
}

async function compressImage(file) {
  if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '')) return file;
  try {
    const bitmap = await createImageBitmap(file).catch(async () => {
      // Fallback decoder for formats createImageBitmap rejects
      const url = URL.createObjectURL(file);
      try {
        const img = await new Promise((res, rej) => {
          const i = new Image();
          i.onload = () => res(i);
          i.onerror = rej;
          i.src = url;
        });
        return img;
      } finally {
        URL.revokeObjectURL(url);
      }
    });
    const w = bitmap.width, h = bitmap.height;
    if (!w || !h) return file;
    const scale = Math.min(1, MAX_DIMENSION / Math.max(w, h));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    if (bitmap.close) bitmap.close();
    const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', JPEG_QUALITY));
    if (!blob) return file;
    // Keep whichever is smaller (tiny images can grow when re-encoded)
    if (blob.size >= file.size) return file;
    const base = (file.name || 'page').replace(/\.[^.]+$/, '');
    return new File([blob], `${base}.jpg`, { type: 'image/jpeg' });
  } catch {
    return file; // never block the user on compression failure
  }
}

/**
 * Row of buttons that open the ORIGINAL scanned pages (stored server-side) in a
 * new tab — used in the patient flow, the nurse detail, and the doctor review
 * so everyone can see exactly what was photographed.
 */
export function ScanPagesRow({ mode = 'staff', sid, token, employerForm }) {
  const { t } = useLang();
  const [busyId, setBusyId] = useState('');
  const [preview, setPreview] = useState(null); // {url, n}
  const ef = employerForm;
  const ids = ef?.file_ids?.length ? ef.file_ids : (ef?.file_id ? [ef.file_id] : []);
  if (!ef || ids.length === 0) return null;

  const openPage = async (fid, n) => {
    setBusyId(fid);
    try {
      const url = mode === 'patient'
        ? `/patient/by-token/${token}/employer-form/file/${fid}`
        : `/submissions/${sid}/employer-form/file/${fid}`;
      const r = await api.get(url, { responseType: 'blob' });
      const obj = URL.createObjectURL(r.data);
      if ((r.data?.type || '').includes('pdf')) {
        // PDFs: download (blob tabs are blocked in sandboxed/iframed browsers)
        const a = document.createElement('a');
        a.href = obj;
        a.download = `employer_form_page_${n}.pdf`;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(obj), 60_000);
      } else {
        // Images: preview inline — works everywhere, no popups
        setPreview({ url: obj, n });
      }
    } catch (e) {
      toast.error(errText(e, t('scan.viewFailed')));
    } finally {
      setBusyId('');
    }
  };

  const closePreview = () => {
    if (preview?.url) setTimeout(() => URL.revokeObjectURL(preview.url), 1000);
    setPreview(null);
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="employer-form-original-scans">
      <span className="text-xs text-muted-foreground flex items-center gap-1">
        <Paperclip className="h-3.5 w-3.5" /> {t('scan.originals')}:
      </span>
      {ids.map((fid, i) => (
        <Button
          key={fid}
          variant="outline"
          size="sm"
          className="h-11 px-3 text-xs"
          onClick={() => openPage(fid, i + 1)}
          disabled={busyId === fid}
          data-testid={`employer-form-view-page-${i}`}
        >
          {busyId === fid ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Eye className="h-3 w-3 mr-1" />}
          {t('scan.page', { n: i + 1 })}
        </Button>
      ))}
      <Dialog open={!!preview} onOpenChange={(o) => !o && closePreview()}>
        <DialogContent className="max-w-2xl" data-testid="employer-form-scan-preview">
          <DialogHeader>
            <DialogTitle className="text-base">{t('scan.originals')} — {preview ? t('scan.page', { n: preview.n }) : ''}</DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="max-h-[70vh] overflow-auto rounded-lg border bg-muted/30 grid place-items-center">
              <img src={preview.url} alt={t('scan.page', { n: preview.n })} className="max-w-full h-auto" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Employer HR-form scanner — shared by the patient intake flow and the nurse portal.
 *
 * mode="patient": uses /patient/by-token/{token}/employer-form (public, token-based)
 * mode="staff":   uses /submissions/{sid}/employer-form (JWT)
 *
 * Genius-Scan-style capture: pages are collected in a local tray (take a photo,
 * add another page, or multi-select files/PDFs) and submitted together in one
 * scan. The AI only reads the employer's form to extract its questions; answers
 * are composed from the record locally — patient data is never sent to the AI.
 */
export function EmployerFormScan({ mode, token, sid, employerForm, locked = false, onChanged, scanRef }) {
  const { t } = useLang();
  const base = mode === 'patient' ? `/patient/by-token/${token}/employer-form` : `/submissions/${sid}/employer-form`;
  const patient = mode === 'patient';
  // Raw scan/upload errors (a Bedrock message, `scan_unavailable_no_baa`, PyMuPDF's
  // "Could not read file: …") are for staff. A patient gets a sentence they can act on.
  const uploadError = (e) => {
    const detail = e?.response?.data?.detail;
    if (patient && typeof detail === 'string' && detail.startsWith('Could not read file')) return t('scan.failToastGeneric');
    return errText(e, t('scan.uploadFailed'));
  };
  const cameraRef = useRef(null);
  const fileRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [answers, setAnswers] = useState({});
  const [pages, setPages] = useState([]); // {id, file, url|null, isPdf}
  const [compressing, setCompressing] = useState(false);
  // R7: true when this batch should be ADDED to the scanned form (finishing a
  // form the page cap cut short) rather than replacing it.
  const [appendMode, setAppendMode] = useState(false);

  // Revoke thumbnail object URLs on unmount only (removePage/submit handle their own cleanup)
  const pagesRef = useRef(pages);
  pagesRef.current = pages;
  useEffect(() => () => { pagesRef.current.forEach((p) => p.url && URL.revokeObjectURL(p.url)); }, []);

  const ef = employerForm;
  const gapQuestions = useMemo(() => {
    if (!ef || ef.status !== 'extracted') return [];
    // A patient is asked only what they personally know; clinical gaps are the office's.
    return (ef.questions || []).filter((q) => q.maps_to === 'none' && (!patient || q.audience === 'patient'));
  }, [ef, patient]);
  // Staff only: AI-drafted answers (`employer_form.suggested`), prefilled but
  // not saved — a human accepts them. Low-confidence ones are a hint, not a value.
  const suggested = (!patient && ef?.suggested) || {};
  const prefill = (qid) => (suggested[qid] && suggested[qid].confidence !== 'low' ? suggested[qid].answer : '');

  const savedAnswers = ef?.answers || {};
  const autoCount = (ef?.questions || []).filter((q) => q.maps_to !== 'none').length;
  const unansweredGaps = gapQuestions.filter((q) => !String(savedAnswers[q.id] || '').trim() && !String(answers[q.id] || '').trim());

  // Typed answers used to live only in this component: Pay/Submit never sent
  // them, so they were lost and the case stuck behind the completeness gate. The
  // patient's answers now save on blur, and the page flushes whatever is left
  // (and refuses to send while photos sit unscanned in the tray) via `scanRef`.
  // `persisted` covers the round trip before the refreshed form arrives.
  const persisted = useRef({});
  const inFlight = useRef(Promise.resolve());
  const unsaved = () => {
    const out = {};
    Object.entries(answers).forEach(([k, v]) => {
      const val = String(v).trim();
      if (val !== String(persisted.current[k] ?? savedAnswers[k] ?? '').trim()) out[k] = val;
    });
    return out;
  };
  const persist = (toSave) => {
    const prior = inFlight.current.catch(() => {});
    if (Object.keys(toSave).length === 0) return prior;
    const run = prior
      .then(() => api.put(`${base}/answers`, { answers: toSave }))
      .then(() => { Object.assign(persisted.current, toSave); });
    inFlight.current = run;
    return run;
  };
  const saveOnBlur = () => {
    if (!patient || locked) return;
    const toSave = unsaved();
    if (Object.keys(toSave).length === 0) return;
    persist(toSave).then(() => onChanged?.(), (e) => toast.error(errText(e, t('scan.answersError'))));
  };
  if (scanRef) scanRef.current = { trayCount: pages.length, flush: () => persist(unsaved()) };
  useEffect(() => () => { if (scanRef) scanRef.current = null; }, [scanRef]);

  const addFiles = async (fileList) => {
    const picked = Array.from(fileList || []);
    if (picked.some(isHeic)) toast.error(t('scan.heicUnsupported'));
    const incoming = picked.filter((f) => !isHeic(f));
    if (incoming.length === 0) return;
    setCompressing(true);
    let processed;
    try {
      processed = await Promise.all(incoming.map(compressImage));
    } finally {
      setCompressing(false);
    }
    setPages((prev) => {
      const next = [...prev];
      for (const f of processed) {
        if (next.length >= MAX_PAGES) { toast.error(t('scan.maxPages', { max: MAX_PAGES })); break; }
        const isPdf = f.type === 'application/pdf' || /\.pdf$/i.test(f.name || '');
        next.push({ id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, file: f, url: isPdf ? null : URL.createObjectURL(f), isPdf });
      }
      const totalMb = next.reduce((s, p) => s + p.file.size, 0) / (1024 * 1024);
      if (totalMb > MAX_TOTAL_MB) { toast.error(t('scan.tooBig', { max: MAX_TOTAL_MB })); return prev; }
      return next;
    });
    if (cameraRef.current) cameraRef.current.value = '';
    if (fileRef.current) fileRef.current.value = '';
  };

  const removePage = (id) => {
    setPages((prev) => {
      const gone = prev.find((p) => p.id === id);
      if (gone?.url) URL.revokeObjectURL(gone.url);
      return prev.filter((p) => p.id !== id);
    });
  };

  const submitPages = async () => {
    if (pages.length === 0) return;
    setScanning(true);
    try {
      const fd = new FormData();
      pages.forEach((p) => fd.append('file', p.file));
      const url = appendMode ? `${base}?append=true` : base;
      const r = await api.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (r.data?.employer_form?.status === 'extracted') {
        toast.success(t('scan.success', { n: r.data.employer_form.questions.length }));
        setPages([]);
        setAppendMode(false);
      } else {
        toast.error((!patient && r.data?.employer_form?.error) || t('scan.failToastGeneric'));
      }
      onChanged?.();
    } catch (e) {
      toast.error(uploadError(e));
    } finally {
      setScanning(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(base);
      toast.success(t('scan.removed'));
      onChanged?.();
    } catch (e) {
      toast.error(errText(e, t('scan.removeFailed')));
    }
  };

  // Plain elements, NOT a component defined in render: a component made here is a
  // new type every render, so React remounted these inputs whenever state changed —
  // "Add the remaining pages" sets appendMode and opens the picker, and the file
  // the patient chose arrived at an input that was no longer in the page.
  const hiddenInputs = (
    <>
      {/* Camera capture — on phones this opens the camera directly (one page at a time) */}
      <input ref={cameraRef} type="file" accept="image/jpeg,image/png" capture="environment" className="hidden" onChange={(e) => addFiles(e.target.files)} data-testid={`employer-form-camera-input-${mode}`} />
      {/* Library / files — multi-select photos or PDFs. Naming JPEG/PNG rather than image/*
          makes iOS transcode HEIC on the way out instead of handing us a file we can't read. */}
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,application/pdf" multiple className="hidden" onChange={(e) => addFiles(e.target.files)} data-testid={`employer-form-input-${mode}`} />
    </>
  );

  const PickButtons = ({ compact = false }) => (
    <div className="flex flex-wrap gap-2">
      <Button variant={compact ? 'outline' : 'default'} size="sm" onClick={() => cameraRef.current?.click()} disabled={scanning || locked} data-testid={`employer-form-take-photo-${mode}`}>
        <Camera className="h-4 w-4 mr-1" /> {compact ? t('scan.addCamera') : t('scan.takePhoto')}
      </Button>
      <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={scanning || locked} data-testid={`employer-form-upload-${mode}`}>
        <FileUp className="h-4 w-4 mr-1" /> {compact ? t('scan.addFiles') : t('scan.upload')}
      </Button>
    </div>
  );

  const PagesTray = () => (
    <div className="space-y-3" data-testid="employer-form-pages-tray">
      <div className="flex flex-wrap gap-2 pt-2 pr-2">
        {pages.map((p, i) => (
          <div key={p.id} className="relative shrink-0" data-testid={`employer-form-page-${i}`}>
            <div className="h-20 w-16 rounded-lg border bg-background overflow-hidden">
              {p.isPdf ? (
                <div className="h-full w-full grid place-items-center bg-secondary/60">
                  <FileText className="h-6 w-6 text-muted-foreground" />
                </div>
              ) : (
                <img src={p.url} alt={t('scan.page', { n: i + 1 })} className="h-full w-full object-cover" />
              )}
              <span className="absolute bottom-0 left-0 right-0 bg-foreground/70 text-background text-xs text-center py-0.5">
                {p.isPdf ? 'PDF' : t('scan.page', { n: i + 1 })}
              </span>
            </div>
            {/* 44px hit area around a 20px visible ×, hanging off the corner so it does not cover the thumbnail */}
            <button
              type="button"
              onClick={() => removePage(p.id)}
              className="absolute -top-2 -right-2 h-11 w-11 grid place-items-center"
              aria-label={t('scan.removePage', { n: i + 1 })}
              data-testid={`employer-form-remove-page-${i}`}
            >
              <span className="h-5 w-5 rounded-full bg-foreground/70 text-background grid place-items-center"><X className="h-3 w-3" /></span>
            </button>
          </div>
        ))}
        {pages.length < MAX_PAGES && (
          <button
            type="button"
            onClick={() => cameraRef.current?.click()}
            className="h-20 w-16 rounded-lg border border-dashed grid place-items-center text-muted-foreground hover:text-foreground hover:border-foreground/40 shrink-0"
            aria-label={t('scan.addPage')}
            data-testid="employer-form-add-page"
          >
            <Plus className="h-5 w-5" />
          </button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={submitPages} disabled={scanning || locked || pages.length === 0} data-testid="employer-form-submit-pages">
          <ScanLine className="h-4 w-4 mr-1" /> {pages.length === 1 ? t('scan.scanNow.one') : t('scan.scanNow.many', { n: pages.length })}
        </Button>
        <PickButtons compact />
      </div>
      <p className="text-xs text-muted-foreground">{t('scan.addAll', { max: MAX_PAGES })}</p>
    </div>
  );

  if (scanning || compressing) {
    return (
      <div className="rounded-xl border bg-accent/40 p-4 flex items-center gap-3" data-testid="employer-form-scanning">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
        <div className="text-sm">
          <p className="font-medium">{compressing ? t('scan.preparing') : t('scan.scanning')}</p>
          <p className="text-xs text-muted-foreground">{compressing ? t('scan.preparingSub') : t('scan.scanningSub')}</p>
        </div>
      </div>
    );
  }

  if (!ef || pages.length > 0) {
    return (
      <div className="rounded-xl border border-dashed bg-secondary/40 p-4 space-y-3" data-testid="employer-form-empty">
        {hiddenInputs}
        <p className="text-sm font-medium flex items-center gap-2"><ScanLine className="h-4 w-4 text-primary" /> {appendMode ? t('scan.addRemainingTitle') : ef ? t('scan.replace') : t('scan.prompt')}</p>
        {appendMode && <p className="text-xs text-muted-foreground">{t('scan.addRemainingHint', { max: MAX_PAGES })}</p>}
        <p className="text-xs text-muted-foreground">
          {t('scan.hint')}
        </p>
        {/* ADR 0006 decision 8: the conditions are stated inline, no checkbox — tapping upload is the acknowledgement. */}
        <p className="text-xs text-foreground/80" data-testid="employer-form-conditions">
          {t('scan.conditions')}
        </p>
        {pages.length > 0 ? <PagesTray /> : <PickButtons />}
        <p className="text-xs text-muted-foreground flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> {t('scan.privacy')}</p>
      </div>
    );
  }

  if (ef.status === 'failed') {
    return (
      <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-4 space-y-2" data-testid="employer-form-failed">
        {hiddenInputs}
        <p className="text-sm font-medium flex items-center gap-2"><AlertCircle className="h-4 w-4 text-destructive" /> {t('scan.failedTitle')}</p>
        <p className="text-xs text-muted-foreground">{patient ? t('scan.failedBodyPatient') : (ef.error || t('scan.failedBody'))}</p>
        <div className="flex flex-wrap gap-2">
          <PickButtons />
          {mode === 'staff' && <Button variant="ghost" size="sm" onClick={remove} disabled={locked} data-testid="employer-form-remove"><Trash2 className="h-4 w-4 mr-1" /> Remove</Button>}
        </div>
      </div>
    );
  }

  const saveAnswers = async () => {
    const toSave = {};
    Object.entries(answers).forEach(([k, v]) => { if (String(v).trim()) toSave[k] = String(v).trim(); });
    if (Object.keys(toSave).length === 0) { toast.info(t('scan.nothingToSave')); return; }
    setSaving(true);
    try {
      await api.put(`${base}/answers`, { answers: toSave });
      Object.assign(persisted.current, toSave);
      toast.success(t('scan.answersSaved'));
      setAnswers({});
      onChanged?.();
    } catch (e) {
      toast.error(errText(e, t('scan.answersError')));
    } finally {
      setSaving(false);
    }
  };

  const drafted = gapQuestions.filter((q) => suggested[q.id] && !String(savedAnswers[q.id] || '').trim() && String(answers[q.id] ?? prefill(q.id)).trim());
  const acceptSuggestions = async () => {
    setSaving(true);
    try {
      await api.put(`${base}/answers`, { answers: Object.fromEntries(drafted.map((q) => [q.id, String(answers[q.id] ?? prefill(q.id)).trim()])) });
      toast.success(t('scan.answersSaved'));
      setAnswers({});
      onChanged?.();
    } catch (e) {
      toast.error(errText(e, t('scan.answersError')));
    } finally {
      setSaving(false);
    }
  };

  // Staff only: a checklist's "None of these" / "Clear" saves straight away (GapChecklist).
  const setGroup = async (picks) => {
    setSaving(true);
    try {
      await api.put(`${base}/answers`, { answers: picks });
      setAnswers((a) => Object.fromEntries(Object.entries(a).filter(([k]) => !(k in picks))));
      onChanged?.();
    } catch (e) {
      toast.error(errText(e, t('scan.answersError')));
    } finally {
      setSaving(false);
    }
  };
  const renderGap = (q) => (
    <div key={q.id} className="space-y-1">
      <Label className="text-xs">{q.question}</Label>
      <Textarea
        rows={2}
        value={answers[q.id] ?? (savedAnswers[q.id] || prefill(q.id))}
        onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
        onBlur={saveOnBlur}
        maxLength={1000}
        placeholder={t('scan.answerPlaceholder')}
        disabled={locked}
        data-testid={`employer-form-gap-${q.id}`}
      />
      {!savedAnswers[q.id] && <SuggestionHint q={{ suggestion: suggested[q.id] }} value={answers[q.id] ?? prefill(q.id)} />}
    </div>
  );

  return (
    <div className="rounded-xl border bg-card p-4 space-y-3" data-testid="employer-form-extracted">
      {hiddenInputs}
      <div className="flex flex-wrap items-center gap-2">
        <CheckCircle2 className="h-4 w-4 text-primary" />
        <span className="text-sm font-medium truncate max-w-[280px]" data-testid="employer-form-title">{ef.form_title || t('scan.untitledForm')}</span>
        {ef.source_organization && <Badge variant="secondary" className="text-xs">{ef.source_organization}</Badge>}
      </div>
      <div className="flex flex-wrap gap-2 text-xs">
        <Badge variant="outline" data-testid="employer-form-qcount">{t('scan.questionsFound', { n: (ef.questions || []).length })}</Badge>
        {(ef.page_count || 0) > 1 && <Badge variant="outline">{t('scan.pagesScanned', { n: ef.page_count })}</Badge>}
        <Badge variant="outline" className="text-primary border-primary/40">{t('scan.autoAnswered', { n: autoCount })}</Badge>
        {gapQuestions.length > 0 && (
          <Badge variant={unansweredGaps.length ? 'destructive' : 'secondary'} data-testid="employer-form-gapcount">
            {unansweredGaps.length ? (unansweredGaps.length === 1 ? t('scan.needInputOne') : t('scan.needInput', { n: unansweredGaps.length })) : t('scan.gapsDone')}
          </Badge>
        )}
      </div>

      <ScanPagesRow mode={mode} sid={sid} token={token} employerForm={ef} />

      {/* R7: the page cap used to drop pages in silence. Name what was left out
          and offer to finish the form in a second pass. */}
      {(ef.pages_skipped || 0) > 0 && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 space-y-2" data-testid="employer-form-skipped">
          <p className="text-xs font-medium flex items-start gap-2">
            <AlertCircle className="h-3.5 w-3.5 text-amber-600 shrink-0 mt-0.5" />
            <span>{t('scan.skippedTitle', { n: ef.pages_skipped, max: ef.max_pages || MAX_PAGES })}</span>
          </p>
          <ul className="text-xs text-muted-foreground pl-5 space-y-0.5">
            {(ef.skipped || []).map((s, i) => (
              <li key={`${s.name}-${i}`} data-testid={`employer-form-skipped-${i}`}>
                {s.scanned > 0
                  ? t('scan.skippedFilePartial', { name: s.name, scanned: s.scanned, pages: s.pages })
                  : t('scan.skippedFileAll', { name: s.name, pages: s.pages })}
              </li>
            ))}
          </ul>
          <p className="text-xs text-muted-foreground">{t('scan.skippedBody')}</p>
          <Button
            size="sm"
            variant="outline"
            onClick={() => { setAppendMode(true); fileRef.current?.click(); }}
            disabled={scanning || locked}
            data-testid="employer-form-add-remaining"
          >
            <Plus className="h-4 w-4 mr-1" /> {t('scan.addRemaining')}
          </Button>
        </div>
      )}

      {gapQuestions.length > 0 && (
        <div className="space-y-3 rounded-lg border bg-secondary/40 p-3">
          <p className="text-xs font-medium text-muted-foreground">{t('scan.gapsIntro')}</p>
          {(patient ? gapQuestions.map((q) => ({ q })) : groupGaps(gapQuestions)).map((b) => (b.group
            ? <GapChecklist key={b.group.key} group={b.group} onSet={setGroup} disabled={saving || locked} renderItem={renderGap} />
            : renderGap(b.q)
          ))}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={saveAnswers} disabled={saving || locked} data-testid="employer-form-save-answers">
              {saving && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />} {t('scan.saveAnswers')}
            </Button>
            {drafted.length > 0 && (
              <Button size="sm" variant="outline" onClick={acceptSuggestions} disabled={saving || locked} data-testid="employer-form-accept-suggestions">
                Accept suggestions ({drafted.length})
              </Button>
            )}
          </div>
        </div>
      )}

      <Collapsible>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="text-xs w-full justify-between" data-testid="employer-form-toggle-questions">
            {t('scan.viewAll')} <ChevronDown className="h-3.5 w-3.5" />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <ul className="mt-2 space-y-1 max-h-64 overflow-y-auto pr-1">
            {(ef.questions || []).map((q) => (
              <li key={q.id} className="text-xs flex items-start gap-2 rounded border bg-background px-2 py-1.5">
                <Badge variant={q.maps_to === 'none' ? 'destructive' : 'secondary'} className="text-xs shrink-0 mt-0.5">
                  {q.maps_to === 'none' ? t('scan.manual') : t('scan.auto')}
                </Badge>
                <span className="text-muted-foreground">{q.question}</span>
              </li>
            ))}
          </ul>
        </CollapsibleContent>
      </Collapsible>

      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={() => { setAppendMode(false); fileRef.current?.click(); }} disabled={scanning || locked} data-testid="employer-form-replace">
          <ScanLine className="h-4 w-4 mr-1" /> {t('scan.replaceBtn')}
        </Button>
        {mode === 'staff' && <Button variant="ghost" size="sm" onClick={remove} disabled={locked} data-testid="employer-form-remove"><Trash2 className="h-4 w-4 mr-1" /> Remove</Button>}
      </div>
    </div>
  );
}
